import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis } from "recharts";
import { buildThemeClasses } from "../theme";
import {
  EMPTY_INSIGHTS,
  fetchActivityInsights,
  formatHours,
  formatMoney,
  rangeDates,
  stageLabel,
  type ActivityInsights,
  type InsightsRange,
} from "../services/activityInsights";

// ===========================================================================
// Tiempo del equipo comercial por prospecto (#1309). Tres gráficas sobre
// `GET /crm/activityInsights`:
//   1. tiempo total por prospecto (todas sus reuniones y horas ligadas);
//   2. tiempo promedio por prospecto en cada etapa del embudo;
//   3. tiempo invertido contra el monto cotizado (negocios vivos del prospecto).
// El rango es por fecha local: quincena, mes o 90 días.
// ===========================================================================

const RANGES: { id: InsightsRange; label: string }[] = [
  { id: "quincena", label: "Quincena" },
  { id: "mes", label: "Mes" },
  { id: "90d", label: "90 días" },
];

const TOP_PROSPECTS = 15;

interface Props {
  isDarkMode: boolean;
  /** Para pruebas: evita la llamada y pinta estos datos. */
  initialData?: ActivityInsights | null;
}

export default function ActividadComercialCharts({ isDarkMode, initialData = null }: Props) {
  const tc = buildThemeClasses(isDarkMode);
  const [range, setRange] = useState<InsightsRange>("mes");
  const [data, setData] = useState<ActivityInsights>(initialData ?? EMPTY_INSIGHTS);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) return;
    let alive = true;
    setLoading(true);
    setError(null);
    fetchActivityInsights(rangeDates(range))
      .then((d) => alive && setData(d))
      .catch((e: { response?: { status?: number }; message?: string }) => {
        if (!alive) return;
        // Sin el endpoint desplegado no hay nada que graficar: no es un error de la persona.
        if (e.response?.status === 404) setData(EMPTY_INSIGHTS);
        else setError(e.message || "No se pudo calcular el tiempo por prospecto");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [range, initialData]);

  const axisTick = { fontSize: 11, fill: isDarkMode ? "#9ca3af" : "#6b7280" };
  const tooltipStyle = {
    background: isDarkMode ? "#1f2430" : "#fff",
    border: `1px solid ${isDarkMode ? "#2d3340" : "#e5e7eb"}`,
    borderRadius: 8,
    fontSize: 12,
    color: isDarkMode ? "#e5e7eb" : "#111827",
  };
  const grid = isDarkMode ? "#333" : "#eee";

  const topProspects = useMemo(
    () => data.byProspect.slice(0, TOP_PROSPECTS).map((p) => ({ ...p, hours: Math.round((p.minutes / 60) * 10) / 10, stageLabel: stageLabel(p.stage) })),
    [data.byProspect],
  );
  const stages = useMemo(() => data.byStage.map((s) => ({ ...s, label: stageLabel(s.stage), avgHours: Math.round((s.avgMinutesPerProspect / 60) * 10) / 10 })), [data.byStage]);
  const scatter = useMemo(() => data.timeVsAmount.map((p) => ({ ...p, hours: Math.round((p.minutes / 60) * 10) / 10, stageLabel: stageLabel(p.stage) })), [data.timeVsAmount]);
  const currency = scatter.find((p) => p.currency)?.currency ?? "MXN";

  const card = `rounded-2xl border p-5 ${tc.cardBorder} ${tc.cardBg}`;
  const empty = (text: string) => <div className={`flex h-[220px] items-center justify-center text-sm ${tc.textSecondary}`}>{text}</div>;

  return (
    <section className="mb-6" data-testid="crm-activity-charts">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className={`text-base font-bold ${tc.text}`}>Tiempo del equipo comercial por prospecto</h2>
          <p className={`text-xs ${tc.textSecondary}`}>
            {loading
              ? "Calculando…"
              : `${data.meta.prospects} prospectos · ${formatHours(data.meta.totalMinutes)} · ${data.meta.meetings} reuniones · ${data.meta.from} → ${data.meta.to}`}
          </p>
        </div>
        <div className={`flex rounded-lg border p-0.5 text-xs ${tc.border} ${tc.cardBg}`} role="tablist" aria-label="Rango">
          {RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              role="tab"
              aria-selected={range === r.id}
              onClick={() => setRange(r.id)}
              className={`rounded-md px-3 py-1.5 font-medium ${range === r.id ? "bg-primary-500 text-white" : tc.textSecondary}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-500">
          <i className="ri-error-warning-line mr-1" />
          {error}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        {/* 1 · tiempo total por prospecto */}
        <div className={card} data-testid="chart-by-prospect">
          <h3 className={`mb-3 text-sm font-semibold ${tc.text}`}>Tiempo total por prospecto</h3>
          {topProspects.length === 0 ? (
            empty("Sin reuniones ni horas ligadas a prospectos en el rango.")
          ) : (
            <div style={{ width: "100%", height: Math.max(220, topProspects.length * 26) }}>
              <ResponsiveContainer>
                <BarChart data={topProspects} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={grid} horizontal={false} />
                  <XAxis type="number" tick={axisTick} tickFormatter={(v) => `${v} h`} />
                  <YAxis type="category" dataKey="name" tick={axisTick} width={120} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: number) => [`${Number(v).toFixed(1)} h`, "Tiempo"]}
                    labelFormatter={(label, payload) => {
                      const p = payload && payload[0] && (payload[0].payload as { meetings: number; stageLabel: string });
                      return p ? `${label} · ${p.meetings} reuniones · ${p.stageLabel}` : String(label);
                    }}
                  />
                  <Bar dataKey="hours" name="Horas" fill="#0084D5" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* 2 · promedio por etapa */}
        <div className={card} data-testid="chart-by-stage">
          <h3 className={`mb-3 text-sm font-semibold ${tc.text}`}>Tiempo promedio por prospecto en cada etapa</h3>
          {stages.length === 0 ? (
            empty("Sin datos por etapa en el rango.")
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={stages} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                  <XAxis dataKey="label" tick={axisTick} interval={0} angle={stages.length > 4 ? -20 : 0} textAnchor={stages.length > 4 ? "end" : "middle"} height={stages.length > 4 ? 56 : 30} />
                  <YAxis tick={axisTick} tickFormatter={(v) => `${v} h`} width={48} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: number) => [`${Number(v).toFixed(1)} h por prospecto`, "Promedio"]}
                    labelFormatter={(label, payload) => {
                      const s = payload && payload[0] && (payload[0].payload as { prospects: number; meetings: number });
                      return s ? `${label} · ${s.prospects} prospectos · ${s.meetings} reuniones` : String(label);
                    }}
                  />
                  <Bar dataKey="avgHours" name="Promedio" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* 3 · tiempo invertido vs monto cotizado */}
        <div className={card} data-testid="chart-time-vs-amount">
          <h3 className={`mb-3 text-sm font-semibold ${tc.text}`}>Tiempo invertido vs. monto cotizado</h3>
          {scatter.length === 0 ? (
            empty("Sin prospectos con tiempo en el rango.")
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                  <XAxis type="number" dataKey="hours" name="Horas" tick={axisTick} tickFormatter={(v) => `${v} h`} />
                  <YAxis type="number" dataKey="amount" name="Monto" tick={axisTick} tickFormatter={(v) => formatMoney(Number(v), currency)} width={80} />
                  <ZAxis type="number" dataKey="meetings" range={[40, 200]} name="Reuniones" />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    cursor={{ strokeDasharray: "3 3" }}
                    content={({ payload }) => {
                      const p = payload && payload[0] && (payload[0].payload as (typeof scatter)[number]);
                      if (!p) return null;
                      return (
                        <div style={tooltipStyle} className="px-3 py-2">
                          <p className="font-semibold">{p.name}</p>
                          <p>{p.stageLabel}</p>
                          <p>
                            {p.hours.toFixed(1)} h · {p.meetings} reuniones
                          </p>
                          <p>{p.amount > 0 ? formatMoney(p.amount, p.currency) : "Sin monto cotizado"}</p>
                        </div>
                      );
                    }}
                  />
                  <Scatter data={scatter} fill="#10B981" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className={`mt-2 text-[11px] ${tc.textSecondary}`}>Cada punto es un prospecto; el tamaño, sus reuniones. El monto suma sus negocios vivos o ganados.</p>
        </div>
      </div>
    </section>
  );
}
